import { useEffect, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import sendNodeCommand from 'store/sendNodeCommand';

interface UpgradeWorkerDialogProps {
  open: boolean;
  nodeAgentId: string;
  instanceId: string;
  workerName: string;
  currentPackageName: string;
  currentVersion?: string;
  onClose: () => void;
  onConfirm: () => void;
}

export default function UpgradeWorkerDialog({
  open,
  nodeAgentId,
  instanceId,
  workerName,
  currentPackageName,
  currentVersion,
  onClose,
  onConfirm,
}: UpgradeWorkerDialogProps) {
  const [newVersion, setNewVersion] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setNewVersion(currentVersion || '');
      setError(null);
      setIsSubmitting(false);
    }
  }, [open, currentVersion]);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await sendNodeCommand(nodeAgentId, 'upgrade_worker', {
        instance_id: instanceId,
        package_version: newVersion.trim() || undefined,
      });
      onConfirm();
      onClose();
    } catch (err) {
      console.error('Failed to upgrade worker:', err);
      setError('Failed to upgrade worker. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={isSubmitting} maxWidth="sm" fullWidth>
      <DialogTitle>Upgrade Worker</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          Upgrade worker <strong>{workerName}</strong>.
          <br />
          All workers sharing this working directory will be restarted.
        </DialogContentText>
        <TextField
          label="Package Name"
          value={currentPackageName}
          fullWidth
          margin="normal"
          disabled
        />
        <TextField
          label="New Version"
          value={newVersion}
          onChange={(e) => setNewVersion(e.target.value)}
          fullWidth
          margin="normal"
          placeholder="e.g. 0.12.0"
          helperText="Leave empty to upgrade to the latest version."
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Upgrading\u2026' : 'Upgrade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
